import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import _ from "lodash";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import WifiIcon from "@mui/icons-material/Wifi";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CircularProgress from "@mui/material/CircularProgress";

import { VerificationLayout } from "../../components/signup/verificationLayout";
import { HeaderContent } from "../../components/signup/headerContent";
import { MiddleContainer } from "../../components/signup/middleContainer";
import { BottomCtaContainer } from "../../components/signup/bottomCtaContainer";

import { useAppSelector, useAppDispatch } from "../../store/hook";
import { setPortalProps } from "../../store/modules/portalSlice";
import { setMyProfile } from "../../store/modules/meSlice";

import * as api from "../../api";

import TagManager from "react-gtm-module";
import { GenerateGTMArgs } from "../../utils/gtmArgs";

const defaultValue = {
  wifiNetwork: "",
  wifiPassword: "",
};

export const WifiDetails = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { id, propertyDetails } = useAppSelector(
    (state) => state.portal || ({} as any)
  );

  const [formData, setFormData] = useState(defaultValue);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (propertyDetails) {
      setFormData({
        wifiNetwork: _.get(propertyDetails, "wifiNetwork", "") || "",
        wifiPassword: _.get(propertyDetails, "wifiPassword", "") || "",
      });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    const nextPage = "/onboarding/hoa-details";
    if (
      _.isEqual(
        formData,
        _.pick(propertyDetails, ["wifiNetwork", "wifiPassword"])
      )
    ) {
      handleSkip();
      return;
    }

    setIsLoading(true);
    try {
      const payload: any = {
        propertyDetails: {
          ...propertyDetails,
          ...formData,
        },
        onboardingStep: nextPage,
      };
      await api.portal.update(id, payload);

      TagManager.dataLayer({
        dataLayer: GenerateGTMArgs("accCreationPage7", {
          wifi_network_added: formData.wifiNetwork,
          wifi_pw_added: formData.wifiPassword,
          page7_continue_clicked: true,
          page7_skip_clicked: false,
        }),
      });

      dispatch(setPortalProps(_.pick(payload, ['propertyDetails'])));
      dispatch(setMyProfile(_.pick(payload, ['onboardingStep'])));
      navigate(nextPage);
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar("Failed to update wifi details. Please try again!", {
        variant: "error",
        onClose: () => { },
        action: (key) => (
          <IconButton
            color="inherit"
            size="small"
            onClick={() => closeSnackbar(key)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const handleSkip = () => {
    const onboardingUpdate = { onboardingStep: "/onboarding/hoa-details" };
    api.portal.update(id, onboardingUpdate).catch((err) => console.error(err));

    TagManager.dataLayer({
      dataLayer: GenerateGTMArgs("accCreationPage7", {
        wifi_network_added: formData.wifiNetwork,
        wifi_pw_added: formData.wifiPassword,
        page7_continue_clicked: false,
        page7_skip_clicked: true,
      }),
    });

    dispatch(setMyProfile(onboardingUpdate));
    navigate("/onboarding/hoa-details");
  };

  return (
    <VerificationLayout
      currentStep={5}
      withBackButton={true}
      handleBack={() => navigate("/onboarding/property-details")}
    >
      <HeaderContent
        handleBack={() => navigate("/onboarding/property-details")}
        headerText="Add your Wi-Fi details"
        headerVariant="h5"
        withMobileHeaderText={true}
        withMobileSubText={true}
        withMobileBack={true}
        currentStep={5}
      />
      <MiddleContainer>
        <FormControl variant="outlined" fullWidth>
          <TextField
            label="Wi-Fi network name"
            type="text"
            id="wifi-start-adornment"
            name="wifiNetwork"
            value={formData.wifiNetwork}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WifiIcon />
                </InputAdornment>
              ),
            }}
            placeholder=" "
            disabled={isLoading}
            autoComplete="off"
          />
        </FormControl>
        <FormControl variant="outlined" fullWidth>
          <TextField
            label="Wi-Fi password"
            type={showPassword ? "text" : "password"}
            name="wifiPassword"
            value={formData.wifiPassword}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
            placeholder=" "
            disabled={isLoading}
            autoComplete="off"
          />
        </FormControl>
        <Box
          sx={{
            padding: "1.5rem",
            width: "295px",
            border: "1px solid #FFEDE0",
            background: "#FFF6F0",
            top: 0,
            left: "calc(100% + 3rem)",
            borderRadius: "8px",
          }}
          position={{
            xs: "static",
            lg: "absolute",
          }}
          marginX="auto"
        >
          <Stack gap={4}>
            <Typography
              variant="body2"
              textAlign="center"
              color={"primary"}
              sx={{
                fontWeight: `700 !important`,
              }}
            >
              Why are we asking for your Wi-Fi?
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              color={"textPrimary"}
            >
              Your solar monitoring system must be connected to your Wi-Fi, so
              we need this information to set up your system. Feel free to
              change your password as soon as we’ve set up your system!
            </Typography>
          </Stack>
        </Box>
      </MiddleContainer>
      <BottomCtaContainer>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => handleSubmit()}
          disabled={isLoading}
        >
          {isLoading && (
            <CircularProgress color="inherit" size={16} thickness={4.5} />
          )}
          Continue
        </Button>
        <Button variant="text" onClick={() => handleSkip()}>
          I’ll do this later
        </Button>
      </BottomCtaContainer>
    </VerificationLayout>
  );
};
